import { VStack, Icon } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC } from 'react';

import LockIconOpen from '@/assets/icons/ic-lock-open.svg';
import LockIcon from '@/assets/icons/ic-lock.svg';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import { ThinLectureCard } from '@/components/ui/shared/lecture/ThinLectureCard/ThinLectureCard';
import { queryLecturesEventRelationships } from '@/fetchers/thinEvents';
import { queryLectureSimilarRelationship } from '@/fetchers/thinLectures';
import { useResource, useResourceRelationship, useResources } from '@/lib/datx-jsonapi-react';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { ThinLecture } from '@/resources/ThinLecture';

const getEventName = (eventId, eventsData) => eventsData?.find((event) => event.id === eventId);

export const SimilarThinLectures: FC = () => {
  const { id } = useLecturePageQueryParams();

  const { data: similarLectures } = useResourceRelationship(queryLectureSimilarRelationship(id));
  const firstSimilarLecture = similarLectures?.[0];

  const { data: lectureEvents } = useResources(
    queryLecturesEventRelationships([firstSimilarLecture] as Array<ThinLecture>)
  );

  const { data: lecturePermission } = useResource(queryLectureAccessPermission(firstSimilarLecture?.id));

  if (!firstSimilarLecture || !lectureEvents || !lecturePermission || lectureEvents.length === 0) {
    return <LectureCardSkeleton />;
  }

  return (
    <VStack spacing={7}>
      {firstSimilarLecture && (
        <NextLink href={`/lecture/${firstSimilarLecture.linkSlug}`} passHref legacyBehavior>
          <ThinLectureCard
            as="a"
            icon={
              <Icon
                as={lecturePermission.restricted ? LockIcon : LockIconOpen}
                h="1.5rem"
                w="1.5rem"
                color={lecturePermission.restricted ? 'gray.500' : 'teal.500'}
              />
            }
            lecture={firstSimilarLecture}
            event={getEventName(firstSimilarLecture.eventId, lectureEvents)}
            w="full"
          />
        </NextLink>
      )}
    </VStack>
  );
};
